import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useAddPaymentMethodsMutation } from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { clsx } from 'clsx';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import Input from 'src/components/Input/Input';
import { Link } from 'react-router-dom';
import { COSSUPORT_TERMS, ERROR_COUNTRIES } from 'src/shared/constants';
import { SelectComponentWithSearch } from 'src/pages/SubscriptionPage/PaymentMethod/AddPaymentMethodPopup/SelectComponentWithSearch/SelectComponentWithSearch';
import Button from 'src/components/Button/Button';
import { Button as NewButton } from 'src/components/ui/button';
import { SelectTaxIdTypeComponentWithSearch } from 'src/pages/SubscriptionPage/PaymentMethod/AddPaymentMethodPopup/SelectTaxIdTypeComponentWithSearch/SelectTaxIdTypeComponentWithSearch';

export const CardType = ['corporate', 'personal'];
const CardVariant = ['main_card', 'backup_card'];

const useOptions = () => {
  return useMemo(
    () => ({
      className: 'testClass',
      style: {
        base: {
          fontSize: '14px',
          fontWeight: '500',
          color: '#05050DCC',
          letterSpacing: '0.025em',
          '::placeholder': {
            color: '#B8C1C6',
          },
        },
        invalid: {
          color: '#FF5151',
        },
      },
    }),
    [],
  );
};

export const SplitElements = ({ handleClose, handleCreateSuccess, submitBtnText = 'Save' }) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const stripe = useStripe();
  const elements = useElements();
  const notification = useNotification();
  const loader = useLoader();

  const options = useOptions();

  const [addPaymentMethod, { isLoading, isSuccess, isError, error }] =
    useAddPaymentMethodsMutation();

  useEffect(() => {
    if (isLoading) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoading]);

  useEffect(() => {
    if (isSuccess) {
      notification.success('Payment method was added successfully.');
      handleCreateSuccess(true);
      handleClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      notification.error('Something went wrong.');
    }
  }, [isError]);

  const {
    handleSubmit,
    trigger,
    control,
    setError,
    watch,
    clearErrors,
    reset,
    formState: { errors, isValid },
  } = useForm<any>({
    defaultValues: {
      card_number: {},
      expiration: {},
      cvc: {},
      card_type: CardType[0],
      card_variant: CardVariant[0],
      name: '',
      address: '',
      country: {},
      state: '',
      city: '',
      zip: '',
    },
    resolver: yupResolver(
      object({
        card_number: object({
          complete: booleanScheme(),
          error: object({
            message: stringScheme(),
          }),
        }).label('Card number'),
        expiration: object({
          complete: booleanScheme(),
          error: object({
            message: stringScheme(),
          }),
        }).label('Expiration date'),
        cvc: object({
          complete: booleanScheme(),
          error: object({
            message: stringScheme(),
          }),
        }).label('CVC'),
        card_type: stringScheme({ required: false }),
        card_variant: stringScheme({ required: false }),
        tax_id: stringScheme({ required: false }).nullable().label('Tax ID'),
        name: stringScheme({ required: true }).label('Name'),
        address: stringScheme({ required: true }).label('Address'),
        city: stringScheme({ required: true }).label('City'),
        tax_id_type: stringScheme({ required: false }).label('Tax ID type'),
        country: object({
          iso2: stringScheme({ required: true }),
          name: stringScheme({ required: true }).label('Country'),
        }).label('Country'),
        state: object({
          state_code: stringScheme({ required: false }),
          name: stringScheme({ required: false }),
        })
          .transform((value, originalValue) => {
            return typeof originalValue === 'string' && originalValue.trim() === ''
              ? undefined
              : value;
          })
          .notRequired()
          .label('State'),
        zip: stringScheme({ required: true }).label('Zip Code'),
      }),
    ),
  });

  const watchAllFields = watch(['card_number', 'expiration', 'cvc']);

  const watchCardType = watch('card_type');
  const watchCountry = watch('country');

  const creditRef = useRef(null);
  const expirationRef = useRef(null);
  const cvcRef = useRef(null);

  const disableSubmitCondition = watchAllFields.some((el) => !el?.complete);

  const handleAddPaymentMethod = async ({
    paymentMethod,
    tax_id_value,
    tax_id_type,
    card_type,
    card_role,
  }) => {
    addPaymentMethod({
      payment_method_id: paymentMethod.paymentMethod.id,
      tax_id_value,
      tax_id_type,
      card_type,
      card_role,
    });
  };

  const handleSubmitCard = async (data) => {
    if (!stripe || !elements || !isValid || disableSubmitCondition) {
      return;
    }

    const payload = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
      billing_details: {
        name: data.name,
        address: {
          line1: data.address,
          line2: '',
          city: data.city,
          state: data?.state?.name || '',
          postal_code: data.zip,
          country: data?.country?.iso2,
        },
      },
    });

    if (payload?.error) {
      return notification.error(payload?.error?.message);
    }

    if (payload?.paymentMethod) {
      await handleAddPaymentMethod({
        paymentMethod: payload,
        tax_id_value: data.tax_id,
        tax_id_type: data.tax_id_type,
        card_type: data.card_type,
        card_role: data.card_variant,
      });
    }
  };

  useEffect(() => {
    async function fetchCountries() {
      const res = await fetch('/api/countries').then((res) => res.json());
      setCountries(res);
    }
    fetchCountries();
  }, []);

  useEffect(() => {
    if (watchCountry) {
      const fetchStates = async () => {
        const res = await fetch(`/api/states?countryId=${(watchCountry as any)?.id}`).then((res) =>
          res.json(),
        );
        setStates(res.states);
      };
      fetchStates();
    }
  }, [watchCountry]);

  useEffect(() => {
    if (watchCountry) {
      if (ERROR_COUNTRIES.some((el) => el === watchCountry.iso2)) {
        setError('country.name', {
          message: 'Invalid address',
        });
        setError('state.name', {
          message: 'Invalid address',
        });
      } else {
        clearErrors('country.name');
        clearErrors('state.name');
      }
    }
  }, [watchCountry]);

  const handleCancel = () => {
    reset();
    handleClose();
  };

  return (
    <form id="add-payment-method-form" onSubmit={handleSubmit(handleSubmitCard)}>
      <Controller
        control={control}
        name="card_type"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <RadioGroup
            onChange={onChange}
            onValueChange={onChange}
            defaultValue={value}
            className="flex space-x-5 mb-[15px]"
          >
            {CardType.map((type, index) => (
              <div key={type} className="flex items-center space-x-2.5">
                <RadioGroupItem value={type} id={`${type}-${index}`} />
                <label htmlFor={`${type}-${index}`} className="cursor-pointer">
                  <p
                    className={clsx(
                      value !== type ? 'opacity-30' : 'opacity-70',
                      'font-medium text-sm text-ebony font-jamjuree hover:opacity-100 transition capitalize',
                    )}
                  >
                    {type}
                  </p>
                </label>
              </div>
            ))}
          </RadioGroup>
        )}
      />
      <Controller
        control={control}
        name="name"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Input
            className="placeholder:!font-medium !rounded-sm-5 border-[#B8C1C666]"
            labelClassName="text-xs text-[#B8C1C6] mt-2"
            type={'text'}
            label={watchCardType === CardType[0] ? 'Company name*' : 'Card holder name*'}
            placeholder={watchCardType === CardType[0] ? 'ABC Technologies Inc.' : 'Anna Jonson'}
            invalidMessage={error?.message}
            value={value}
            isValid={!error?.message}
            onChange={onChange}
            onBlur={() => trigger('name')}
          />
        )}
      />
      <Controller
        control={control}
        name="address"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Input
            className="!rounded-sm-5 border-[#B8C1C666]"
            labelClassName="mt-2"
            type={'text'}
            label={'Billing address*'}
            placeholder={'123 Main Street, Suite 400'}
            invalidMessage={error?.message}
            value={value}
            isValid={!error?.message}
            onChange={onChange}
            onBlur={() => trigger('address')}
          />
        )}
      />
      <div className="pt-2 flex gap-1">
        <div className="w-full">
          <label className="text-xs text-[#B8C1C6]">Country*</label>
          <div className="relative">
            <Controller
              control={control}
              name="country"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <SelectComponentWithSearch
                    setSelected={onChange}
                    items={countries}
                    inputPlaceholder="Search Country"
                    placeholder="Country"
                    emptyMessage="No Country found."
                    btnClassName={
                      (error as any)?.name?.message &&
                      '!text-error [&_span]:opacity-100 !border-error'
                    }
                  />
                  {(error as any)?.name?.message && (
                    <div className="max-md:hidden error-message absolute flex items-center justify-center px-3.5 py-2 right-1 top-1/2 transform -translate-y-1/2 h-[calc(100%-8px)] bg-error_bg rounded-sm-5 overflow-hidden">
                      <p className="text-sm font-semibold text-error">
                        {(error as any)?.name?.message}
                      </p>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </div>
        {!!states.length && (
          <div className="w-full">
            <label className="text-xs text-[#B8C1C6]">State</label>
            <div className="relative">
              <Controller
                control={control}
                name="state"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <SelectComponentWithSearch
                      items={states}
                      setSelected={onChange}
                      inputPlaceholder="Search State"
                      placeholder="State"
                      emptyMessage="No State found."
                      btnClassName={
                        (error as any)?.name?.message &&
                        '!text-error [&_span]:opacity-100 !border-error'
                      }
                    />
                    {(error as any)?.name?.message && (
                      <div className="max-md:hidden error-message absolute flex items-center justify-center px-3.5 py-2 right-1 top-1/2 transform -translate-y-1/2 h-[calc(100%-8px)] bg-error_bg rounded-sm-5 overflow-hidden">
                        <p className="text-sm font-semibold text-error">
                          {(error as any)?.name?.message}
                        </p>
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex pt-2 gap-1">
        <Controller
          control={control}
          name="city"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className="w-full">
              <Input
                className={'!rounded-sm-5'}
                labelClassName="text-xs text-[#B8C1C6]"
                type={'text'}
                label="City*"
                placeholder="San Francisco"
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('city')}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="zip"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <div className="w-full">
              <Input
                labelClassName="text-xs text-[#B8C1C6]"
                className={'!rounded-sm-5'}
                type={'text'}
                label="Zip code*"
                placeholder="94105"
                invalidMessage={error?.message}
                value={value}
                isValid={!error?.message}
                onChange={onChange}
                onBlur={() => trigger('zip')}
              />
            </div>
          )}
        />
      </div>
      {watchCardType === CardType[0] && (
        <div className="flex gap-1 mt-2 items-end">
          <div className="w-full">
            <label className="text-xs text-grey_450">Tax ID type</label>
            <div className="relative">
              <Controller
                control={control}
                name="tax_id_type"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <SelectTaxIdTypeComponentWithSearch
                      setSelected={onChange}
                      countryItems={countries}
                      inputPlaceholder="Search Tax ID type"
                      placeholder="Tax ID type"
                      emptyMessage="No Type found."
                      btnClassName={
                        (error as any)?.name?.message &&
                        '!text-error [&_span]:opacity-100 !border-error'
                      }
                    />
                    {(error as any)?.name?.message && (
                      <div className="max-md:hidden error-message absolute flex items-center justify-center px-3.5 py-2 right-1 top-1/2 transform -translate-y-1/2 h-[calc(100%-8px)] bg-error_bg rounded-sm-5 overflow-hidden">
                        <p className="text-sm font-semibold text-error">
                          {(error as any)?.name?.message}
                        </p>
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div className="w-full">
            <div className="relative">
              <Controller
                control={control}
                name="tax_id"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    labelClassName="text-xs !mb-0"
                    className={'!rounded-sm-5'}
                    type={'text'}
                    label={'Tax ID'}
                    placeholder={'123 456 789'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    onBlur={() => trigger('tax_id')}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}
      <div className="pt-2">
        <label className="text-grey_450">
          Credit / debit card number *
          <div
            ref={creditRef}
            className={clsx(
              'rounded-sm-5 border transition duration-300 px-3 py-[10px] bg-white relative',
              watchAllFields[0]?.error?.message && 'border-error',
            )}
          >
            <Controller
              control={control}
              name="card_number"
              render={({ field: { onChange } }) => (
                <>
                  <CardNumberElement
                    options={options}
                    onReady={() => {
                      clearErrors('card_number');
                    }}
                    onChange={(event) => {
                      onChange({
                        complete: event?.complete,
                        error: {
                          message: event?.error?.message,
                        },
                      });
                      if (event?.error?.message) {
                        setError('card_number', {
                          message: event?.error?.message,
                        });
                      }
                    }}
                    onBlur={() => {
                      creditRef?.current.classList.remove('border-green_750');
                      creditRef?.current.classList.remove('[&_.error-message]:hidden');
                    }}
                    onFocus={() => {
                      creditRef?.current.classList.add('border-green_750');
                      creditRef?.current.classList.add('[&_.error-message]:hidden');
                    }}
                  />
                  {watchAllFields[0]?.error?.message && (
                    <div className="z-10 max-md:hidden error-message absolute flex items-center justify-center px-2.5 py-2 w-full left-0 -bottom-1 transform translate-y-[100%] bg-error_bg rounded-sm-5 overflow-hidden">
                      <p className="text-sm font-semibold text-error">
                        {watchAllFields[0]?.error?.message}
                      </p>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </label>
      </div>
      <div className="flex w-full gap-1 pt-2">
        <label className="w-full text-grey_450">
          Expiration month and year *
          <div
            ref={expirationRef}
            className={clsx(
              'rounded-sm-5 border transition duration-300 px-3 py-2.5 bg-white relative',
              watchAllFields[1]?.error?.message && 'border-error',
            )}
          >
            <Controller
              control={control}
              name="expiration"
              render={({ field: { onChange } }) => (
                <>
                  <CardExpiryElement
                    options={options}
                    onReady={() => {
                      clearErrors('expiration');
                    }}
                    onChange={(event) => {
                      onChange({
                        complete: event?.complete,
                        error: {
                          message: event?.error?.message,
                        },
                      });
                      if (event?.error?.message) {
                        setError('expiration', {
                          message: event?.error?.message,
                        });
                      }
                    }}
                    onBlur={() => {
                      expirationRef?.current.classList.remove('border-green_750');
                      expirationRef?.current.classList.remove('[&_.error-message]:hidden');
                    }}
                    onFocus={() => {
                      expirationRef?.current.classList.add('border-green_750');
                      expirationRef?.current.classList.add('[&_.error-message]:hidden');
                    }}
                  />
                  {watchAllFields[1]?.error?.message && (
                    <div className="z-10 max-md:hidden error-message absolute flex items-center justify-center px-2.5 py-2 w-full left-0 -bottom-1 transform translate-y-[100%] bg-error_bg rounded-sm-5 overflow-hidden">
                      <p className="text-sm font-semibold text-error">
                        {watchAllFields[1]?.error?.message}
                      </p>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </label>
        <label className="w-full text-grey_450">
          CVC/CVV *
          <div
            ref={cvcRef}
            className={clsx(
              'rounded-sm-5 border transition duration-300 px-3 py-2.5 bg-white relative',
              watchAllFields[2]?.error?.message && 'border-error',
            )}
          >
            <Controller
              control={control}
              name="cvc"
              render={({ field: { onChange } }) => (
                <>
                  <CardCvcElement
                    options={options}
                    onReady={() => {
                      clearErrors('cvc');
                    }}
                    onChange={(event) => {
                      onChange({
                        complete: event?.complete,
                        error: {
                          message: event?.error?.message,
                        },
                      });
                      if (event?.error?.message) {
                        setError('cvc', {
                          message: event?.error?.message,
                        });
                      }
                    }}
                    onBlur={() => {
                      cvcRef?.current.classList.remove('border-green_750');
                      cvcRef?.current.classList.remove('[&_.error-message]:hidden');
                    }}
                    onFocus={() => {
                      cvcRef?.current.classList.add('border-green_750');
                      cvcRef?.current.classList.add('[&_.error-message]:hidden');
                    }}
                  />
                  {watchAllFields[2]?.error?.message && (
                    <div className="z-10 max-md:hidden error-message absolute flex items-center justify-center px-2.5 py-2 w-full left-0 -bottom-1 transform translate-y-[100%] bg-error_bg rounded-sm-5 overflow-hidden">
                      <p className="text-sm font-semibold text-error">
                        {watchAllFields[2]?.error?.message}
                      </p>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </label>
      </div>
      <Controller
        control={control}
        name="card_variant"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <RadioGroup
            onChange={onChange}
            onValueChange={onChange}
            defaultValue={value}
            className="flex space-x-5 my-4"
          >
            {CardVariant.map((variant, index) => (
              <div key={variant} className="flex items-center space-x-2.5">
                <RadioGroupItem value={variant} id={`${variant}-${index}`} />
                <label htmlFor={`${variant}-${index}`} className="cursor-pointer">
                  <p
                    className={clsx(
                      value !== variant ? 'opacity-30' : 'opacity-70',
                      'font-medium text-sm text-ebony font-jamjuree hover:opacity-100 transition',
                    )}
                  >
                    <span className="capitalize">{variant.split('_')[0]}</span> <span>card</span>
                  </p>
                </label>
              </div>
            ))}
          </RadioGroup>
        )}
      />
      <div>
        <p className="font-jamjuree font-medium text-xs">
          <span className="opacity-30">
            By adding this Payment Method, you confirm that you have read, understood, and agreed to
            be bound by the
          </span>{' '}
          <Link
            to={COSSUPORT_TERMS}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-100 opacity-70 transition"
          >
            Terms of Services.
          </Link>
        </p>
      </div>
      <div className="flex gap-2.5 w-full justify-end pt-4">
        <NewButton variant="outline" onClick={handleCancel} className="min-w-24">
          Cancel
        </NewButton>
        <NewButton
          disabled={!stripe || !isValid || disableSubmitCondition}
          type="submit"
          form="add-payment-method-form"
          className="min-w-24"
        >
          {submitBtnText}
        </NewButton>
      </div>
    </form>
  );
};
